import { Trash2 } from 'lucide-react';
import { formatPrice, calculateBuyPrice, cn, getActivePrices, getDefaultEdition } from '../lib/utils';
import { MiniPriceChart } from './MiniPriceChart';
import { CardImage } from './CardImage';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import type { Card, Mode } from '../lib/types';

interface ComparisonPanelProps {
  cards: Card[];
  mode: Mode;
  buyPercent: number;
  onRemoveCard: (card: Card) => void;
  onClose: () => void;
}

export function ComparisonPanel({
  cards,
  mode,
  buyPercent,
  onRemoveCard,
  onClose,
}: ComparisonPanelProps) {
  const getDisplayPrice = (price: number): string => {
    if (mode === 'buy') {
      return formatPrice(calculateBuyPrice(price, buyPercent));
    }
    return formatPrice(price);
  };

  const getCardPrices = (card: Card) => {
    const edition = getDefaultEdition(card);
    const activePrices = getActivePrices(card, edition);
    return {
      nm: activePrices?.nm,
      lp: activePrices?.lp,
      mp: activePrices?.mp,
    };
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-auto p-0">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center border-b border-outline bg-card px-6 py-4">
          <DialogTitle>Compare Cards</DialogTitle>
        </div>

        {/* Mode indicator */}
        <div className="flex justify-center border-b border-outline py-3">
          <Badge variant={mode === 'buy' ? 'tertiary' : 'default'} className="px-3 py-1 text-sm">
            {mode === 'buy' ? `Buy Offer (${buyPercent}%)` : 'Sell Price'}
          </Badge>
        </div>

        {/* Comparison grid */}
        <div className="p-6">
          <div
            className={cn(
              'grid gap-4',
              cards.length === 1 && 'grid-cols-1 max-w-sm mx-auto',
              cards.length === 2 && 'grid-cols-2',
              cards.length === 3 && 'grid-cols-3'
            )}
          >
            {cards.map((card) => {
              const prices = getCardPrices(card);

              return (
                <div
                  key={card.id}
                  className="flex flex-col rounded-lg border border-outline bg-muted p-4"
                >
                  {/* Card image */}
                  <div className="mx-auto w-24 overflow-hidden rounded-lg">
                    {card.image_url ? (
                      <CardImage
                        src={card.image_url}
                        alt={card.name}
                        className="w-full"
                      />
                    ) : (
                      <div className="flex aspect-[3/4] items-center justify-center bg-card text-xs text-muted-foreground">
                        No image
                      </div>
                    )}
                  </div>

                  {/* Card info */}
                  <div className="mt-3 text-center">
                    <h3 className="truncate text-sm font-semibold text-foreground">
                      {card.name}
                    </h3>
                    <p className="truncate text-xs text-muted-foreground">
                      {card.set_name} · #{card.card_number}
                    </p>
                  </div>

                  {/* Mini price chart */}
                  <div className="mt-3">
                    <MiniPriceChart
                      cardName={card.name}
                      setName={card.set_name}
                      cardNumber={card.card_number}
                    />
                  </div>

                  {/* Prices */}
                  <div className="mt-3 space-y-2">
                    {prices.nm != null && (
                      <div className="flex items-center justify-between rounded bg-card px-2 py-1.5">
                        <span className="text-xs text-muted-foreground">NM</span>
                        <span
                          className={cn(
                            'text-sm font-semibold',
                            mode === 'buy' ? 'text-tertiary' : 'text-primary'
                          )}
                        >
                          {getDisplayPrice(prices.nm)}
                        </span>
                      </div>
                    )}
                    {prices.lp != null && (
                      <div className="flex items-center justify-between rounded bg-card px-2 py-1.5">
                        <span className="text-xs text-muted-foreground">LP</span>
                        <span
                          className={cn(
                            'text-sm font-semibold',
                            mode === 'buy' ? 'text-tertiary' : 'text-primary'
                          )}
                        >
                          {getDisplayPrice(prices.lp)}
                        </span>
                      </div>
                    )}
                    {prices.mp != null && (
                      <div className="flex items-center justify-between rounded bg-card px-2 py-1.5">
                        <span className="text-xs text-muted-foreground">MP</span>
                        <span
                          className={cn(
                            'text-sm font-semibold',
                            mode === 'buy' ? 'text-tertiary' : 'text-primary'
                          )}
                        >
                          {getDisplayPrice(prices.mp)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Remove button */}
                  <button
                    onClick={() => onRemoveCard(card)}
                    className="mt-4 flex items-center justify-center gap-2 rounded-full border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive transition-colors hover:bg-destructive/20"
                  >
                    <Trash2 className="h-4 w-4" />
                    Remove
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
